import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { MutationFeedback } from '@/integrations/tanstack-query/types'
import type { AffiliateJoinSource } from '@/lib/analytics/affiliate-tracking'
import { trackAffiliateProgramJoined } from '@/lib/analytics/affiliate-tracking'
import { apiClient } from '@/lib/api-client'
import { useAffiliateWelcomeStore } from '@/stores/affiliate-welcome'

import type { GetAffiliateDashboardResponse } from '../get-affiliate-dashboard'

import type { JoinAffiliateProgramResponse } from './join-affiliate-program.types'

const joinAffiliateProgram =
  async (): Promise<JoinAffiliateProgramResponse> => {
    const response = await apiClient.post<JoinAffiliateProgramResponse>(
      '/v1/affiliation/affiliates',
    )
    return response.data
  }

interface UseJoinAffiliateProgramOptions {
  /**
   * Which door the caller is. Required so the one join event can still be
   * split by entry point — the callers used to fire it themselves, which is
   * how it ended up duplicated and undiscriminated.
   */
  source: AffiliateJoinSource
  feedback?: MutationFeedback
}

/**
 * Joining is the same event through either door — the sidebar's activation
 * dialog or the section's own landing — so everything that must happen exactly
 * once per join happens here rather than at the call sites.
 */
export const useJoinAffiliateProgram = (
  options: UseJoinAffiliateProgramOptions,
) => {
  const queryClient = useQueryClient()
  const openWelcome = useAffiliateWelcomeStore((state) => state.openWelcome)

  return useMutation({
    mutationFn: joinAffiliateProgram,
    meta: {
      invalidatesQuery: ['affiliate-dashboard'],
      ...options.feedback,
    },
    onSuccess: () => {
      // Membership is settled by this response, so the cached gate is patched
      // here rather than left to the refetch: without it the caller navigates
      // into the program while `joined: false` is still cached, and the
      // section renders its landing underneath the welcome modal until the
      // refetch lands. The invalidation above still confirms it against the
      // server.
      queryClient.setQueryData<GetAffiliateDashboardResponse>(
        ['affiliate-dashboard'],
        (previous) => previous && { ...previous, joined: true },
      )
      trackAffiliateProgramJoined(options.source)
      openWelcome()
    },
  })
}
